// OpenAI service (client wrapper) aligned with gmb-ranking-dashboard API shape
// Uses backend AI endpoint under the hood for reliability in browser environments

import ApiService from '../api';

const callAI = async (prompt, model = 'gemini-1.5-flash', generationConfig = {}) => {
console.log('🚀 [OpenAI Service] Calling AI with prompt length:', prompt.length);
console.log('🤖 [OpenAI Service] Using model:', model);
  
  const resp = await ApiService.post('/customer/ai/generate', {
    prompt,
    model,
    generationConfig: {
      temperature: 0.8,
      topK: 40,
      topP: 0.95,
      maxOutputTokens: 1024,  // Increased to prevent extended thinking token exhaustion
      ...generationConfig
    }
  });
  
  const text = resp?.data?.text || resp?.text || '';
console.log('✅ [OpenAI Service] Received response:', text);
console.log('📏 [OpenAI Service] Response length:', text.length, 'characters');
  
  return String(text || '').trim();
};

export const generateDescription = async (businessName, category) => {
  const name = businessName || 'Business';
  const cat = category || 'business';
  const prompt = `Generate a compelling and SEO-friendly business description for a company named "${name}" which is in the "${cat}" category. The description should be professional, inviting, and approximately 150-200 words long. Focus on unique selling points and customer benefits.`;
  return await callAI(prompt);
};

const normalizeArrayInput = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map(item => String(item || '').trim())
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/[,|]/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

const formatAddress = (address) => {
  if (!address) return '';
  if (typeof address === 'string') {
    return address.replace(/\s+/g, ' ').replace(/,\s*,/g, ', ').trim();
  }
  if (typeof address === 'object') {
    const parts = [];
    if (Array.isArray(address.addressLines)) {
      parts.push(address.addressLines.filter(Boolean).join(', '));
    }
    if (address.locality) parts.push(address.locality);
    if (address.administrativeArea) parts.push(address.administrativeArea);
    if (address.postalCode) parts.push(address.postalCode);
    return parts.filter(Boolean).join(', ');
  }
  return '';
};

const sanitizeHashtagCandidates = (text) => {
  if (!text) return [];
  return text
    .split(/[\s,|]+/)
    .map(token => token.trim())
    .filter(Boolean)
    .map(token => (token.startsWith('#') ? token : `#${token}`))
    .map(token => `#${token.slice(1).replace(/[^a-z0-9]/gi, '')}`)
    .map(token => token.length > 1 ? token : '')
    .filter(Boolean);
};

export const generatePost = async (input) => {
  let params = input;
  if (typeof params === 'string' || !params) {
    params = { businessName: params };
  }

  const {
    businessName = 'our business',
    businessCategory = 'local business',
    businessAddress,
    topicType = 'standard update',
    callToAction = '',
    actionUrl = '',
    keywords = [],
    imageHints = '',
    prompt,
    tone = 'professional yet friendly'
  } = params;

  const resolvedAddress = formatAddress(businessAddress);
  const keywordList = normalizeArrayInput(keywords);
  const keywordLine = keywordList.length
    ? `Weave these focus keywords naturally into the copy (no hashtags): ${keywordList.join(', ')}.`
    : 'Pick 2-3 SEO-friendly keywords based on the details and weave them naturally (no hashtags).';
  const addressLine = resolvedAddress
    ? `Mention the business address: ${resolvedAddress}.`
    : 'Invite readers to visit the business and reference the local area.';
  const actionLine = callToAction
    ? `End with a call-to-action that nudges people to ${callToAction.toLowerCase()}.`
    : 'Close with a motivating call-to-action for nearby customers.';
  const linkLine = actionUrl
    ? `If it adds value, reference this URL: ${actionUrl}`
    : '';

const defaultPrompt = `You are a senior social media strategist crafting a Google Business Profile caption for an uploaded image. Task: provide description for this post for Google Business Profile post; hashtags will be added later.

Business Snapshot:
- Name: ${businessName}
- Category: ${businessCategory}
${resolvedAddress ? `- Address: ${resolvedAddress}\n` : ''}- Topic focus: ${topicType}

Visual Context:
${imageHints || 'Assume the image highlights the key product, service, or team moment described by the topic.'}

Writing Rules:
1. Keep it between 70-130 words in a ${tone} voice.
2. Reference what the reader sees in the photo before transitions into the offer or update.
3. ${keywordLine}
4. ${addressLine}
5. ${actionLine}
6. Use at most two relevant emojis.
7. Never mention internal filenames, template codes, or placeholder text.
8. Do NOT include hashtags (they will be added later).
${linkLine ? `9. ${linkLine}` : ''}

Return ONLY the caption text (no labels or bullet points).`;

  const finalPrompt = prompt || defaultPrompt;

  return await callAI(finalPrompt, 'gemini-2.5-flash', { maxOutputTokens: 600 });
};

export const generateReviewReply = async (businessName, reviewText, rating) => {
  const name = businessName || 'our business';
  const r = Number(rating || 5);
  let prompt = '';
  if (r === 5) {
    prompt = `Generate an enthusiastic and appreciative reply to this 5-star customer review for "${name}". Thank the customer sincerely and mention something specific they enjoyed if possible.\n\nCustomer Review: "${reviewText}"\n\nReply:`;
  } else if (r <= 2) {
    prompt = `Generate an empathetic and professional reply to this negative customer review (rated ${r}/5) for "${name}". Apologize for their experience, show concern, and offer to resolve the issue. Avoid being defensive.\n\nCustomer Review: "${reviewText}"\n\nReply:`;
  } else {
    prompt = `Generate a professional and courteous reply to this ${r}-star customer review for "${name}". Thank them for their feedback and acknowledge any points they made.\n\nCustomer Review: "${reviewText}"\n\nReply:`;
  }
  // Use gemini-1.5-flash and higher token limit to avoid extended thinking consuming all tokens
  return await callAI(prompt, 'gemini-1.5-flash', { maxOutputTokens: 1024 });
};

export const generateHashtags = async ({ summary, keywords = [], businessName = '', businessAddress = '' }) => {
  if (!summary) return '';

  const keywordText = normalizeArrayInput(keywords).join(', ');
  const prompt = `You are a social media strategist. Read the caption below and produce 3-4 branded/local hashtags that match its themes. Mix business name, location, and service keywords where relevant.

Caption:
${summary}

Context:
- Business: ${businessName || 'Local business'}
- Address/City: ${businessAddress || 'Local area'}
- Keywords: ${keywordText || 'General services'}

Rules:
- Output ONE line with the hashtags separated by spaces.
- Each hashtag must start with #, contain only letters or numbers (no punctuation), and be unique.
- Prioritize readable TitleCase or camelCase (e.g., #HappyGurpurab, #AmbalaElectronics).
- Do NOT repeat words already adjacent in the caption more than once.
- Avoid generic tags like #instagood or #sale.

Return format: #TagOne #TagTwo #TagThree`;

  const raw = await callAI(prompt, 'gemini-1.5-flash', { maxOutputTokens: 200 });
  const tags = sanitizeHashtagCandidates(raw);
  return Array.from(new Set(tags)).slice(0, 4).join(' ');
};

export const formatHashtags = (raw) => sanitizeHashtagCandidates(raw).slice(0, 4);

export default {
  generateDescription,
  generatePost,
  generateReviewReply,
  generateHashtags,
};

export const generateCategorySuggestions = async (businessName, primaryCategory, location = '') => {
  const name = businessName || 'Business';
  const primary = primaryCategory || 'General Business';
  const loc = location ? ` in ${location}` : '';
  const prompt = `Suggest up to 5 additional Google Business Profile categories for a business named "${name}" with primary category "${primary}"${loc}. Return ONLY a comma-separated list of category names, no explanations.`;
  const raw = await callAI(prompt, 'gemini-2.5-flash', { maxOutputTokens: 300 });
  const items = String(raw)
    .replace(/^\s*\[|\]\s*$/g, '')
    .split(/,|\n/)
    .map(s => s.replace(/^[-•\s]+/, '').trim())
    .filter(Boolean);
  // Deduplicate and limit
  return Array.from(new Set(items)).slice(0, 5);
};

export const generateLabelSuggestions = async (businessName, primaryCategory, location = '', existingLabels = []) => {
  const name = businessName || 'Business';
  const primary = primaryCategory || 'General Business';
  const loc = location ? ` in ${location}` : '';
  const exclude = Array.isArray(existingLabels) && existingLabels.length
    ? ` Exclude these labels: ${existingLabels.join(', ')}.`
    : '';
  const prompt = `Suggest up to 10 concise internal labels (1-3 words each) for a Google Business Profile for a business named "${name}" with primary category "${primary}"${loc}. Focus on useful internal organization like service types, zones/areas, priorities, campaigns, VIP or premium flags. Return ONLY a comma-separated list of label names, no explanations.${exclude}`;
  const raw = await callAI(prompt, 'gemini-2.5-flash', { maxOutputTokens: 300 });
  const items = String(raw)
    .replace(/^\s*\[|\]\s*$/g, '')
    .split(/,|\n/)
    .map(s => s.replace(/^[-•\s]+/, '').trim())
    .filter(Boolean);
  const forbidden = new Set((existingLabels || []).map(x => String(x).toLowerCase()));
  const cleaned = items
    .map(x => x.replace(/\s{2,}/g, ' '))
    .filter(x => x.split(' ').length <= 4)
    .filter(x => !forbidden.has(x.toLowerCase()));
  return Array.from(new Set(cleaned)).slice(0, 10);
};

export const generateQnASuggestions = async (businessName, category = 'business', services = [], location = '') => {
  const name = businessName || 'Business';
  const svc = Array.isArray(services) && services.length ? `Services: ${services.join(', ')}` : '';
  const loc = location ? `City/Area: ${location}` : '';
  const prompt = `Generate 5 Google Business Profile Q&A pairs for a business. Each pair should have a short, customer-friendly question and a concise factual answer (max 50 words). Focus on what customers typically ask. Output as lines in the format:\nQ: <question>\nA: <answer>\n\nBusiness: ${name}\nCategory: ${category}\n${svc}\n${loc}`;
  const raw = await callAI(prompt, 'gemini-2.5-flash', { maxOutputTokens: 700 });
  const lines = String(raw).split(/\r?\n/).map(s => s.trim()).filter(Boolean);
  const pairs = [];
  let currentQ = '';
  for (const line of lines) {
    if (/^Q\s*:\s*/i.test(line)) {
      currentQ = line.replace(/^Q\s*:\s*/i, '').trim();
    } else if (/^A\s*:\s*/i.test(line)) {
      const a = line.replace(/^A\s*:\s*/i, '').trim();
      if (currentQ) {
        pairs.push({ question: currentQ, answer: a });
        currentQ = '';
      }
    }
  }
  return pairs.slice(0, 5);
};



